import { CSSProperties } from 'react';
import { Cosmetic } from '../../api/payment/store.api';

/** Preset hiệu ứng avatar (khớp Style seed: glow/pulse/epic/legendary). */
export type EffectPreset = 'glow' | 'pulse' | 'epic' | 'legendary';

/** Suy ra preset từ cosmetic (ưu tiên style; fallback theo code item). */
export const effectPresetOf = (c?: Cosmetic | null): EffectPreset | null => {
  const s = (c?.style || '').toLowerCase();
  if (s === 'glow' || s === 'pulse' || s === 'epic' || s === 'legendary') {
    return s as EffectPreset;
  }
  const code = (c?.code || '').toLowerCase();
  if (code.includes('legendary')) return 'legendary';
  if (code.includes('epic')) return 'epic';
  if (code.includes('rare')) return 'pulse';
  if (code.includes('basic')) return 'glow';
  return null;
};

/** className + style cho vòng ring quanh avatar theo preset. Ring là phần tử absolute inset-0 rounded-full. */
export const effectRing: Record<
  EffectPreset,
  { className: string; style: CSSProperties }
> = {
  glow: {
    className: 'pointer-events-none absolute inset-0 z-20 rounded-full',
    style: { boxShadow: '0 0 0 2px #38bdf8, 0 0 10px 2px rgba(56,189,248,.65)' },
  },
  pulse: {
    className: 'pointer-events-none absolute inset-0 z-20 rounded-full animate-pulse',
    style: { boxShadow: '0 0 0 2px #22d3ee, 0 0 12px 3px rgba(34,211,238,.7)' },
  },
  epic: {
    className: 'pointer-events-none absolute inset-0 z-20 rounded-full',
    style: { boxShadow: '0 0 0 2px #a855f7, 0 0 14px 3px rgba(168,85,247,.75)' },
  },
  legendary: {
    className: 'pointer-events-none absolute inset-0 z-20 rounded-full animate-pulse',
    style: {
      boxShadow:
        '0 0 0 2px #f59e0b, 0 0 0 4px rgba(251,191,36,.45), 0 0 18px 5px rgba(245,158,11,.85)',
    },
  },
};

/** Màu nametag (mã hex từ Style seed). Null nếu không có. */
export const nametagColorOf = (c?: Cosmetic | null): string | null => {
  const s = (c?.style || '').trim();
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(s) ? s : null;
};

/** Bậc độ hiếm của KHUNG avatar (khớp Rarity store: Common→Mythic). */
export type FrameRarity = 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';

/** Tên "huy hiệu" tiếng Việt theo bậc — hiện khi rê chuột vào nametag/khung avatar. */
export const RARITY_LABEL_VI: Record<FrameRarity, string> = {
  common: 'Cổ Điển',
  rare: 'Hiếm',
  epic: 'Sử Thi',
  legendary: 'Huyền Thoại',
  mythic: 'Thần Thoại',
};

/** Suy ra bậc khung từ cosmetic đang gắn (ưu tiên code, fallback style). Null nếu không có. */
export const frameRarityOf = (c?: Cosmetic | null): FrameRarity | null => {
  if (!c) return null;
  const s = `${c.code || ''} ${c.style || ''}`.toLowerCase();
  if (s.includes('mythic') || s.includes('thanthoai') || s.includes('than_thoai')) return 'mythic';
  if (s.includes('legendary') || s.includes('huyenthoai')) return 'legendary';
  if (s.includes('epic') || s.includes('suthi')) return 'epic';
  if (s.includes('rare') || s.includes('hiem') || s === 'pulse') return 'rare';
  if (s.includes('basic') || s.includes('common') || s.includes('coban') || s.includes('glow')) return 'common';
  return null;
};

/** Bậc độ hiếm của NAMETAG (từ code: basic/pro/special/legendary/mythic). Null nếu không gắn. */
export const nametagRarityOf = (c?: Cosmetic | null): FrameRarity | null => {
  if (!c) return null;
  const s = `${c.code || ''} ${c.style || ''}`.toLowerCase();
  if (s.includes('mythic') || s.includes('thanthoai')) return 'mythic';
  if (s.includes('legendary') || s.includes('huyenthoai')) return 'legendary';
  if (s.includes('special') || s.includes('epic') || s.includes('suthi')) return 'epic';
  if (s.includes('pro') || s.includes('rare') || s.includes('hiem')) return 'rare';
  // có gắn nametag nhưng không khớp code → mặc định bậc Cổ Điển (common)
  return 'common';
};
